/**
 * PlanAndCoinsTab — Plan & Coins tab body for the parent dashboard.
 *
 * Implements US-012 / T4 + T5: replaces the previous Plan & Coins tab content
 * (which only rendered CoinBalancePanels) with:
 *   (a) SubscriptionStatusCard — renewal date + monthly coin count, or
 *       'No active subscription' state when monthlyCoinsResetDate is null (AC-1)
 *   (b) CoinBalancePanels — two-bucket wallet display (CCM-02 preserved verbatim)
 *   (c) Subscribe button → POST /stripe/checkout/subscription → window.location.href (AC-2)
 *       Shown ONLY when there is NO active subscription.
 *   (d) Manage subscription button → confirm modal → POST /stripe/portal → window.open (AC-3)
 *       Shown ONLY when there IS an active subscription (replaces Subscribe).
 *   (e) Statement descriptor text 'CODELOGY* THINKOTTER' near payment buttons (AC-4)
 *   (f) Top-up packs grid — rendered config-driven from useTopupPacks (AC-5, CCM-04)
 *       Shown ONLY when there is an active subscription, positioned ABOVE the manage
 *       subscription section (top-ups add to an active plan).
 *       Per-pack Buy button → POST /stripe/checkout/topup → window.location.href (AC-2)
 *       TBD-price packs render a disabled 'Coming soon' button (prevents guaranteed 4xx)
 *
 * Active-subscription detection: balance.monthlyCoinsResetDate !== null (same signal as AC-1).
 *
 * Modified US-013 / T1: successUrl for subscription and top-up checkouts now carries
 *   the ?checkout=success marker so ParentDashboardPage can detect the post-checkout
 *   return and show the CheckoutReturnBanner (AC-1).
 *   cancelUrl remains the plain returnUrl (no marker) — a cancelled checkout must NOT
 *   trigger the success banner.
 *   createBillingPortal(returnUrl) is unchanged — the portal return is not a checkout.
 *
 * KB lessons enforced:
 *   TO-LL-036: all Stripe action errors are caught and surfaced as inline actionError;
 *              never swallowed silently. Portal 404 is caught distinctly.
 *   TO-LL-013: successUrl/cancelUrl/returnUrl use window.location.origin + ROUTES.PARENT_DASHBOARD;
 *              no new hardcoded path string literals. (US-013 T1: only the query suffix is added,
 *              the ROUTES base remains unchanged.)
 *
 * CCM-02: CoinBalancePanels is reused verbatim — two distinct panels, never merged.
 * CCM-03: this component mounts only inside ParentDashboardPage which is behind
 *         ParentGuard. Students are redirected before this ever renders.
 * CCM-04: packs rendered from useTopupPacks API response, never hardcoded.
 *
 * Navigation:
 *   Subscribe/Buy  → window.location.href = checkoutUrl (full-page redirect)
 *   Manage plan    → blank tab pre-opened synchronously before any await, then portalUrl
 *                    assigned via tab.location.href after await (BUG-20260626-175853)
 *   successUrl     = window.location.origin + ROUTES.PARENT_DASHBOARD + ?checkout=success
 *   cancelUrl      = window.location.origin + ROUTES.PARENT_DASHBOARD (no marker)
 *   portalReturnUrl = window.location.origin + ROUTES.PARENT_DASHBOARD (no marker)
 *
 * Modified US-001 (change-plans, T1+T2): "Change plan" entry point (Option B).
 *   - handleChangePlan clones the handleManagePlan pre-open-blank-tab portal pattern
 *     (BUG-20260626-175853) but passes flow='change_plan' and shows NO interim modal
 *     (AC-001.3). The previously warm "Manage subscription" CTA is demoted to a quiet
 *     "Manage billing & cancel" link that still opens CancelConfirmModal unchanged
 *     (AC-001.4).
 *   - Portal-return refetch: a gated document 'visibilitychange' listener (gated by
 *     portalOpenedRef so it does not fire on unrelated tab switches) invalidates the
 *     ['parent','dashboard'] React Query cache key on return, plus one delayed ~4s
 *     second invalidation to catch a lagging invoice.paid webhook (AC-001.5). A light
 *     "updating…" affordance covers the gap. This mirrors the existing delayed-refetch
 *     pattern in ParentDashboardPage.tsx (US-013 checkout banner, :235-247) and the
 *     ['parent','dashboard'] invalidation pattern in useChildMutations.ts:101.
 *   - isPastDue is a pure derived read of subscriptionStatus === 'past_due'; renders a
 *     quiet "payment pending" hint only — no coin/plan-identity change (AC-001.6,
 *     BR-002; identity/coins converge only on invoice.paid).
 *   - BR-001: only the enum-ish flow hint is sent; no price/plan is ever passed by
 *     the client — Stripe's configured product set is the sole source of allowed plans.
 *
 * KB lessons enforced (US-001):
 *   TO-LL-003/TO-LL-007: createBillingPortal's returned string is read as-is
 *     (response.data.portalUrl) — never renamed to .url (see billing.ts:147-154).
 *   TO-LL-036: handleChangePlan's popup-blocked / backend-failure paths set a visible
 *     actionError and close the stray blank tab — never a silent no-op.
 *   TO-LL-013: reuses the existing returnUrl const (ROUTES.PARENT_DASHBOARD); no new
 *     hardcoded path literal is added for the flow hint.
 *
 * Modified US-002 (change-plans, T2+T3): scheduled-downgrade state + "Keep {plan}".
 *   - handleKeepPlan is a two-line-changed clone of handleChangePlan: it calls
 *     createBillingPortal(returnUrl) with NO flow param (portal HOME, BR-004) so
 *     Stripe surfaces its own "cancel this scheduled change" control, and reuses
 *     the SAME portalOpenedRef so the ALREADY-SHIPPED US-001 visibilitychange
 *     refetch effect (unchanged) reverts the card once TS-006 clears the pending
 *     fields server-side (AC-002.4). No new effect, no new endpoint, no modal.
 *   - The shared date-cell fork (isCancelScheduled ? 'Cancels on' : 'Renews on')
 *     gains a THIRD ordered branch: isCancelScheduled (unchanged, highest
 *     precedence) > isPendingDowngrade > default 'Renews on' — so the 'Cancels on'
 *     rendering stays byte-for-byte unaffected (AC-002.1) and 'Renews on' is
 *     preserved when no pending change exists (AC-002.2). The reassurance caption
 *     and 'Keep {plan}' button are gated independently on isPendingDowngrade so the
 *     cancel and pending-downgrade signals coexist without clobbering each other.
 *   - BR-003: pendingPlanKey/pendingPlanEffectiveDate are informational only — no
 *     entitlement/coin decision is derived from them here.
 *
 * KB lessons enforced (US-002):
 *   TO-LL-036: handleKeepPlan's popup-blocked / backend-failure paths set a visible
 *     actionError and close the stray blank tab — never a silent no-op.
 *   TO-LL-013: reuses the existing returnUrl const; no new hardcoded path literal.
 *   TO-LL-044: pendingPlanKey/pendingPlanName/pendingPlanEffectiveDate are read with
 *     the exact camelCase keys declared on OtterCoinBalance (types/coins.ts).
 *   TO-LL-020: the pending signal is re-read from balance on every render — never
 *     cached independently — so a server-side clear always propagates on refetch.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { CoinBalancePanels } from '@/components/CoinBalancePanels';
import { SubscriptionPlans } from '@/components/SubscriptionPlans';
import { LoadingState } from '@/components/LoadingState';
import { ErrorState } from '@/components/ErrorState';
import { EmptyState } from '@/components/EmptyState';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useTopupPacks } from '@/hooks/useTopupPacks';
import {
  createSubscriptionCheckout,
  createTopupCheckout,
  createBillingPortal,
} from '@/services/billing';
import { isPackPurchasable } from '@/types/billing';
import { ROUTES } from '@/constants/routes';
import type { OtterCoinBalance } from '@/types/coins';
import type { TopupPack } from '@/types/billing';
import { Loader2 } from 'lucide-react';
import otterCoinSvg from '@/assets/otter-coin.svg';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface PlanAndCoinsTabProps {
  /** The parent's two-bucket Otter Coin balance (from useParentDashboard) */
  balance: OtterCoinBalance;
  /** Whether to show the welcome banner (passed through to CoinBalancePanels, CCM-02) */
  isNewFamily: boolean;
  /** Callback when the welcome banner is dismissed */
  onDismissWelcome?: () => void;
  /**
   * Configured welcome-coin amount (coin_config welcomeCoinAmount) — passed straight
   * through to CoinBalancePanels so the welcome banner reads the live grant count.
   * Optional; when omitted the banner falls back to the top-up balance.
   */
  welcomeCoinAmount?: number;
  /**
   * TS-009: the parent's current subscription plan display name (e.g. "Practice"),
   * resolved server-side from activePlanKey. null when there is no active plan or it
   * could not be resolved — the combined "Your Subscription" card then falls back to a
   * generic "Active plan" label. Shown in the Plan field of the subscription card.
   */
  planName?: string | null;
  /** TS-009: 'active' | 'cancelled' | null. Reserved for future status nuance. */
  subscriptionStatus?: string | null;
}

// ─── Renewal date helper ──────────────────────────────────────────────────────

/**
 * Format a renewal date (ISO 8601 string) as a human-readable renewal label
 * (en-SG "26 Jun 2026"), or null when the date is absent/malformed. Used by
 * the combined "Your Subscription" card below (replaces the former standalone
 * SubscriptionStatusCard).
 * BUG-20260703-130127 (2026-07-03): now fed balance.nextBillingDate (the real
 * Stripe current_period_end) rather than monthlyCoinsResetDate — the helper
 * itself is unchanged, only its caller's source field changed.
 */
function formatRenewalDate(resetDate: string | null | undefined): string | null {
  if (!resetDate) return null;
  try {
    const d = new Date(resetDate);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleDateString('en-SG', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return null;
  }
}

// ─── TopupPackCard ────────────────────────────────────────────────────────────

interface TopupPackCardProps {
  pack: TopupPack;
  isPending: boolean;
  onBuy: (packId: string) => void;
}

/**
 * A single top-up pack card showing coins, price, and a Buy button.
 *
 * Implements US-012 / T4 / AC-2 / AC-5.
 *
 * Launch-pricing (2026-07-17): when the pack carries a genuine, higher
 * usualPriceSgd, a struck-through anchor renders above the live price —
 * the same treatment (and the same `usual > live` guard) as the subscription
 * cards in SubscriptionPlans.tsx (:117). CCM-04 holds: the anchor comes from
 * the API response, never a hardcoded frontend map.
 *
 * The Buy button is disabled when:
 *   - isPending: this pack's purchase is in-flight (spinner shown)
 *   - !isPackPurchasable(pack): stripePriceId starts with 'TBD_' — renders 'Coming soon'
 *     to prevent a guaranteed 4xx on click (TS-011 rejects TBD price IDs).
 */
function TopupPackCard({ pack, isPending, onBuy }: TopupPackCardProps) {
  const purchasable = isPackPurchasable(pack);
  const priceFmt = `S$${pack.priceSgd.toFixed(2)}`;
  // Only show the struck-through anchor when there is a genuine, higher usual
  // price — never advertise a "discount" that isn't one.
  const usualPrice =
    typeof pack.usualPriceSgd === 'number' && pack.usualPriceSgd > pack.priceSgd
      ? pack.usualPriceSgd.toFixed(2)
      : null;

  return (
    <Card className="flex-1 min-w-[140px]">
      <CardContent className="pt-4 pb-4 flex flex-col items-center gap-2 text-center">
        <img
          src={otterCoinSvg}
          alt="Otter Coin"
          className="h-8 w-8"
          aria-hidden="true"
        />
        <span className="text-2xl font-bold text-amber-600">{pack.coins}</span>
        <span className="text-xs text-gray-500">coins</span>
        <div className="flex flex-col items-center">
          {usualPrice && (
            <span className="text-xs font-medium text-gray-400">
              Usual <span className="line-through">S${usualPrice}</span>
            </span>
          )}
          <span className="text-sm font-semibold text-gray-700">{priceFmt}</span>
        </div>
        {purchasable ? (
          <Button
            size="sm"
            variant="warm"
            className="h-11 w-full mt-1"
            disabled={isPending}
            onClick={() => onBuy(pack.packId)}
            aria-label={`Buy ${pack.coins} coins for ${priceFmt}`}
          >
            {isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              'Buy'
            )}
          </Button>
        ) : (
          <Button
            size="sm"
            variant="outline"
            className="h-11 w-full mt-1"
            disabled
            aria-label={`${pack.coins} coins — coming soon`}
          >
            Coming soon
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

// ─── CancelConfirmModal ────────────────────────────────────────────────────────

interface CancelConfirmModalProps {
  isOpen: boolean;
  isPending: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Lightweight confirm modal for the Manage subscription action.
 *
 * Implements US-012 / T4 / AC-3: describes consequences before opening the
 * Stripe customer portal. No shadcn AlertDialog is available; built from
 * existing Card + Button primitives.
 *
 * Consequences described (per AC-3):
 *   - Subscription continues until end of current period
 *   - Coins are not refunded
 */
function CancelConfirmModal({
  isOpen,
  isPending,
  onConfirm,
  onCancel,
}: CancelConfirmModalProps) {
  if (!isOpen) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
    >
      <Card className="w-full max-w-sm">
        <CardContent className="pt-6 pb-6">
          <h2 id="modal-title" className="text-base font-semibold text-gray-900 mb-2">
            Manage your subscription
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            You&apos;ll be taken to the Stripe customer portal where you can update your plan or
            cancel. Please note:
          </p>
          <ul className="text-sm text-gray-600 list-disc pl-4 mb-5 space-y-1">
            <li>Your subscription continues until the end of the current billing period.</li>
            <li>Coins already granted are not refunded when you cancel.</li>
          </ul>
          <div className="flex gap-3 justify-end">
            <Button variant="outline" size="sm" className="h-11" onClick={onCancel} disabled={isPending}>
              Go back
            </Button>
            <Button size="sm" className="h-11" onClick={onConfirm} disabled={isPending}>
              {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Open billing portal'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── PlanAndCoinsTab (main export) ────────────────────────────────────────────

/**
 * PlanAndCoinsTab — the Plan & Coins tab body for /parent (ROUTES.PARENT_DASHBOARD).
 *
 * Implements US-012 / T4 + T5.
 *
 * Replaces the previous Plan & Coins tab content (CoinBalancePanels only) with a full
 * subscription + top-up purchase UX. Extracted into a dedicated component to keep
 * ParentDashboardPage lean.
 *
 * State managed locally:
 *   pendingAction  — 'subscribe' | 'portal' | packId | null (disables clicked button)
 *   showCancelModal — whether the manage-plan confirm modal is open
 *   actionError    — inline error message for any failed Stripe action
 *
 * Navigation (KB TO-LL-013):
 *   successUrl/cancelUrl = window.location.origin + ROUTES.PARENT_DASHBOARD
 *   Subscribe/Buy  → window.location.href (full-page redirect to Stripe checkout)
 *   Manage plan    → blank tab pre-opened synchronously, then tab.location.href = portalUrl
 *                    after await (BUG-20260626-175853 pre-open pattern)
 */
export function PlanAndCoinsTab({
  balance,
  isNewFamily,
  onDismissWelcome,
  welcomeCoinAmount,
  planName,
  // BUG-20260703-160011 (2026-07-03): destructure the already-delivered
  // subscriptionStatus prop so the active-subscriber gate below can honour
  // it for legacy already-cancelled records (see hasActiveSubscription).
  subscriptionStatus,
}: PlanAndCoinsTabProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Local action state — tracks which button is pending to show spinner + disable
  const [pendingAction, setPendingAction] = useState<string | null>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // US-001 (T1): portal-return refetch state. portalOpenedRef gates the
  // visibilitychange handler so it only refetches after THIS component opened a
  // portal tab (never on an unrelated tab switch). isUpdating drives the light
  // "updating…" affordance (AC-001.5) while the post-return refetch is in flight.
  const portalOpenedRef = useRef(false);
  const [isUpdating, setIsUpdating] = useState(false);

  // Pack list from config-driven API endpoint (AC-5, CCM-04)
  const { packs, isLoading: packsLoading, isError: packsError, refetch: packsRefetch } = useTopupPacks();

  // TO-LL-013: successUrl and cancelUrl built from ROUTES constant, not a string literal.
  // US-013 (T1): checkoutSuccessUrl appends ?checkout=success so ParentDashboardPage can
  // detect the post-checkout return and show CheckoutReturnBanner (AC-1). The base URL uses
  // ROUTES.PARENT_DASHBOARD — only the query suffix is new (enforces TO-LL-013).
  // cancelUrl keeps the plain returnUrl (no marker); portal returnUrl also plain (no banner).
  const returnUrl = `${window.location.origin}${ROUTES.PARENT_DASHBOARD}`;
  const checkoutSuccessUrl = `${window.location.origin}${ROUTES.PARENT_DASHBOARD}?checkout=success`;

  // ─── Stripe action handlers ────────────────────────────────────────────────

  /**
   * Subscribe button handler.
   *
   * Implements US-012 / T4 / AC-2.
   * Modified US-013 / T1: successUrl now carries ?checkout=success marker so the dashboard
   *   shows CheckoutReturnBanner on return (AC-1). cancelUrl remains plain (no marker).
   * KB TO-LL-036: error is set in the catch block, never swallowed.
   * KB TO-LL-013: URL base uses ROUTES constant; only the query suffix differs from cancelUrl.
   */
  async function handleChoosePlan(planKey: string) {
    setActionError(null);
    setPendingAction(planKey);
    try {
      const url = await createSubscriptionCheckout(planKey, checkoutSuccessUrl, returnUrl);
      window.location.href = url; // Full-page redirect to Stripe checkout
    } catch (err) {
      // TO-LL-036: surface the error to the user; never swallow
      const msg = (err as { message?: string })?.message ?? 'Failed to start checkout. Please try again.';
      setActionError(msg);
      setPendingAction(null);
    }
  }

  /**
   * Buy pack button handler.
   *
   * Implements US-012 / T4 / AC-2.
   * Modified US-013 / T1: successUrl now carries ?checkout=success marker so the dashboard
   *   shows CheckoutReturnBanner on return (AC-1). cancelUrl remains plain (no marker).
   * KB TO-LL-036: error is set in the catch block, never swallowed.
   * KB TO-LL-013: URL base uses ROUTES constant; only the query suffix differs from cancelUrl.
   */
  async function handleBuy(packId: string) {
    setActionError(null);
    setPendingAction(packId);
    try {
      const url = await createTopupCheckout(packId, checkoutSuccessUrl, returnUrl);
      window.location.href = url; // Full-page redirect to Stripe checkout
    } catch (err) {
      // TO-LL-036: surface the error; never swallow
      const msg = (err as { message?: string })?.message ?? 'Failed to start checkout. Please try again.';
      setActionError(msg);
      setPendingAction(null);
    }
  }

  /**
   * Manage plan confirm handler (called from inside the modal after the parent confirms).
   *
   * Implements US-012 / T4 / AC-3.
   * 404 NOT_FOUND (no stripeCustomerId yet) → friendly 'Subscribe first' message.
   * KB TO-LL-036: all paths set actionError and clear pending — never swallow.
   *
   * BUG-20260626-175853: pre-open blank tab synchronously BEFORE any await so the
   * browser user-gesture context is still active. Do NOT pass 'noopener' — that
   * forces window.open to return null on Chromium (HTML Living Standard), making
   * handle-based location assignment impossible. Use portalTab.opener = null instead
   * to preserve the no-opener security posture without losing the handle.
   */
  async function handleManagePlan() {
    setActionError(null);
    setPendingAction('portal');

    // BUG-20260626-175853: Step 1 — open blank tab synchronously while user gesture
    // is still active. window.open AFTER an await forfeits the gesture context and
    // is silently blocked. Do NOT pass 'noopener' (returns null on Chromium).
    const portalTab = window.open('', '_blank');

    // BUG-20260626-175853: Step 2 — null check (popup blocked). Surface inline error
    // and bail out early (TO-LL-036: no silent failure path).
    if (portalTab === null) {
      setActionError('Please allow pop-ups in your browser to open the billing portal.');
      setShowCancelModal(false);
      setPendingAction(null);
      return;
    }

    // BUG-20260626-175853: Step 3 — sever opener for security (replaces 'noopener'
    // feature flag without losing the tab handle).
    portalTab.opener = null;

    try {
      // Step 4: await the portal URL now that the blank tab is safely pre-opened.
      const url = await createBillingPortal(returnUrl);
      setShowCancelModal(false);
      setPendingAction(null);
      // Assign the resolved URL to the pre-opened tab. Wrap in try/catch for the rare
      // case where the user closed the blank tab before the fetch completed.
      try {
        portalTab.location.href = url;
      } catch {
        portalTab.close();
        setActionError('Failed to open billing portal. Please try again.');
      }
    } catch (err) {
      // Step 5: close the stray blank tab on backend failure (no stray empty tab).
      portalTab.close();
      const errObj = err as { code?: string; message?: string };
      // 404 / NOT_FOUND → parent has no stripeCustomerId (hasn't subscribed yet)
      if (errObj?.code === 'NOT_FOUND') {
        setActionError('Subscribe first to manage billing.');
      } else {
        // TO-LL-036: all other errors surfaced, never swallowed
        const msg = errObj?.message ?? 'Failed to open billing portal. Please try again.';
        setActionError(msg);
      }
      setShowCancelModal(false);
      setPendingAction(null);
    }
  }

  /**
   * Change-plan handler — opens the Stripe portal deep-linked to the plan-switch
   * screen with NO interim modal.
   *
   * Implements US-001 (change-plans) / T1 / AC-001.3.
   * Near-clone of handleManagePlan (:396-446) reusing the exact
   * BUG-20260626-175853 pre-open-blank-tab pattern (pre-open before any await,
   * sever opener, close-on-failure, actionError never swallowed — TO-LL-036).
   * The ONLY behavioural differences from handleManagePlan:
   *   - passes flow='change_plan' to createBillingPortal (TO-LL-003/007: still
   *     reads response.data.portalUrl, never .url)
   *   - never calls setShowCancelModal — no interim modal is shown (BR-001: the
   *     app never accepts/sends a client-supplied price/plan; Stripe's portal
   *     owns plan selection)
   *   - sets portalOpenedRef.current = true on a successful open, gating the
   *     portal-return refetch effect below (AC-001.5)
   */
  async function handleChangePlan() {
    setActionError(null);
    setPendingAction('change-plan');

    // BUG-20260626-175853 pattern reused: Step 1 — open blank tab synchronously
    // while the user gesture is still active.
    const portalTab = window.open('', '_blank');

    // Step 2 — null check (popup blocked). TO-LL-036: no silent failure path.
    if (portalTab === null) {
      setActionError('Please allow pop-ups in your browser to open the billing portal.');
      setPendingAction(null);
      return;
    }

    // Step 3 — sever opener for security.
    portalTab.opener = null;

    try {
      // Step 4: request the change-plan deep-link (flow='change_plan').
      const url = await createBillingPortal(returnUrl, 'change_plan');
      setPendingAction(null);
      try {
        portalTab.location.href = url;
        // AC-001.5: mark that a portal tab was opened so the visibilitychange
        // effect below refetches ['parent','dashboard'] when the parent returns.
        portalOpenedRef.current = true;
      } catch {
        portalTab.close();
        setActionError('We couldn’t open plan management. Please try again.');
      }
    } catch (err) {
      // Step 5: close the stray blank tab on backend failure.
      portalTab.close();
      const errObj = err as { code?: string; message?: string };
      // 404 / NOT_FOUND → parent has no stripeCustomerId / no active subscription.
      if (errObj?.code === 'NOT_FOUND') {
        setActionError('Subscribe first to manage billing.');
      } else {
        // TO-LL-036: all other errors surfaced, never swallowed.
        const msg = errObj?.message ?? 'We couldn’t open plan management. Please try again.';
        setActionError(msg);
      }
      setPendingAction(null);
    }
  }

  /**
   * Keep-plan handler — opens the Stripe portal at HOME (no flow) so Stripe
   * surfaces its own "cancel this scheduled change" control.
   *
   * Implements US-002 (change-plans) / T2 / AC-002.3 / AC-002.4.
   * Two-line-changed clone of handleChangePlan (:465-510): (a) pendingAction
   * key is 'keep-plan' instead of 'change-plan'; (b) createBillingPortal is
   * called WITHOUT the flow arg (portal HOME, BR-004 — same door as "Manage
   * billing & cancel"). Everything else — pre-open blank tab, opener=null,
   * popup-blocked bail, close-on-failure, actionError never swallowed
   * (TO-LL-036) — is reused verbatim. Sets portalOpenedRef.current = true on
   * a successful open so the EXISTING US-001 visibilitychange effect below
   * (unchanged) refetches ['parent','dashboard'] on return; once TS-006 has
   * cleared pendingPlanKey server-side, the card reverts to 'Renews on' via
   * re-render (AC-002.4). No new effect, no new endpoint, no modal.
   */
  async function handleKeepPlan() {
    setActionError(null);
    setPendingAction('keep-plan');

    // BUG-20260626-175853 pattern reused: Step 1 — open blank tab synchronously
    // while the user gesture is still active.
    const portalTab = window.open('', '_blank');

    // Step 2 — null check (popup blocked). TO-LL-036: no silent failure path.
    if (portalTab === null) {
      setActionError('Please allow pop-ups in your browser to open the billing portal.');
      setPendingAction(null);
      return;
    }

    // Step 3 — sever opener for security.
    portalTab.opener = null;

    try {
      // Step 4: request the portal HOME session — NO flow arg (AC-002.3/BR-004).
      const url = await createBillingPortal(returnUrl);
      setPendingAction(null);
      try {
        portalTab.location.href = url;
        // AC-002.4: mark that a portal tab was opened so the EXISTING
        // visibilitychange effect below refetches ['parent','dashboard']
        // when the parent returns (reused unchanged from US-001).
        portalOpenedRef.current = true;
      } catch {
        portalTab.close();
        setActionError('We couldn’t open plan management. Please try again.');
      }
    } catch (err) {
      // Step 5: close the stray blank tab on backend failure.
      portalTab.close();
      const errObj = err as { code?: string; message?: string };
      // 404 / NOT_FOUND → parent has no stripeCustomerId / no active subscription
      // (unlikely for an active subscriber but preserved for parity).
      if (errObj?.code === 'NOT_FOUND') {
        setActionError('Subscribe first to manage billing.');
      } else {
        // TO-LL-036: all other errors surfaced, never swallowed.
        const msg = errObj?.message ?? 'We couldn’t open plan management. Please try again.';
        setActionError(msg);
      }
      setPendingAction(null);
    }
  }

  /**
   * Portal-return refetch effect.
   *
   * Implements US-001 (change-plans) / T1 / AC-001.5.
   * Detects the parent's return from the Stripe portal tab via the document
   * 'visibilitychange' event (the portal opens in a NEW tab per the pre-open
   * pattern, so returning to this dashboard tab fires visibilitychange). Gated
   * by portalOpenedRef so unrelated tab switches never trigger a refetch
   * (performance consideration — at most 2 fetches per portal round-trip).
   *
   * On return: invalidates the shared ['parent','dashboard'] React Query cache
   * key (same pattern as useChildMutations.ts:101 / useCreateChild.ts:88) so
   * useParentDashboard refetches with zero prop drilling and no edit to
   * ParentDashboardPage. Schedules one delayed (~4s) second invalidation to
   * catch a lagging invoice.paid webhook write (mirrors the US-013 delayed
   * checkout-banner refetch in ParentDashboardPage.tsx:235-247). isUpdating
   * drives the light "updating…" affordance while this is in flight.
   *
   * Full cleanup on unmount / re-run: removes the listener and clears the
   * delayed-invalidate timeout — no setState-after-unmount (performance
   * consideration: no dangling listeners/timers).
   */
  useEffect(() => {
    let delayedInvalidateTimer: ReturnType<typeof setTimeout> | null = null;

    function handleVisibilityChange() {
      if (document.visibilityState === 'visible' && portalOpenedRef.current) {
        setIsUpdating(true);
        queryClient.invalidateQueries({ queryKey: ['parent', 'dashboard'] });
        // Webhook-lag safety net: one delayed second invalidation (~4s) so an
        // upgraded balance that lags behind invoice.paid still converges.
        delayedInvalidateTimer = setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: ['parent', 'dashboard'] });
          setIsUpdating(false);
        }, 4000);
        portalOpenedRef.current = false;
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (delayedInvalidateTimer) clearTimeout(delayedInvalidateTimer);
    };
  }, [queryClient]);

  // US-001 (T1): quiet "payment pending" hint gate — pure derived read of the
  // existing subscriptionStatus prop. AC-001.6 / BR-002 / FR-9.3: display-only;
  // no coin/plan-identity change is rendered until invoice.paid converges the
  // state server-side. Reads the handle_payment_failed signal already delivered
  // on subscriptionStatus — no new backend call.
  const isPastDue = subscriptionStatus === 'past_due';

  // ─── Render ────────────────────────────────────────────────────────────────

  // Active-subscription detection: a non-null monthly-coin reset date means an active
  // subscription cycle is in place (unchanged signal — preserves existing behaviour).
  // BUG-20260703-130127: CRITICAL — this MUST stay on monthlyCoinsResetDate, NOT
  // nextBillingDate. Existing subscribers without nextBillingDate yet (pre-fix,
  // self-healing at next renewal) must still show the Active card state.
  // BUG-20260703-160011 (2026-07-03): AND-guard with the authoritative
  // subscriptionStatus so a subscriptionStatus === 'cancelled' record short-
  // circuits to the No-subscription state even when it still carries a stale
  // monthlyCoinsResetDate — i.e. a legacy record cancelled before the backend
  // REMOVE-clear (users_repository.py mark_subscription_cancelled) shipped.
  // Going forward the backend clear makes monthlyCoinsResetDate null on real
  // cancellation, so this short-circuit is a defensive companion, not the
  // primary fix.
  const hasActiveSubscription =
    subscriptionStatus !== 'cancelled' && balance.monthlyCoinsResetDate !== null;

  // Human-readable renewal label for the "Renews on" fact (null when absent/malformed).
  // BUG-20260703-130127 (2026-07-03): now reads the real next-billing date
  // (nextBillingDate) instead of the coin-grant timestamp (monthlyCoinsResetDate).
  const renewalLabel = formatRenewalDate(balance.nextBillingDate);

  // BUG-20260703-154837 (2026-07-03): cancel-scheduled detection. balance.cancelAt
  // is self-sufficient (carries its own effective end date), so this drives off
  // it alone — NOT the unused subscriptionStatus prop. When absent/null, the
  // existing renewing-state render is unchanged (isCancelScheduled === false).
  const isCancelScheduled = !!balance.cancelAt;
  const cancelLabel = formatRenewalDate(balance.cancelAt);

  // US-002 (change-plans, T2): scheduled-downgrade derived state (BR-003:
  // informational only — no entitlement/coin decision reads these fields).
  // isPendingDowngrade is re-derived from balance on every render (never
  // cached independently) so a server-side clear (TS-006) always propagates
  // on the next dashboard refetch (TO-LL-020).
  const isPendingDowngrade = !!balance.pendingPlanKey;
  // Edge case: pendingPlanName unresolved -> fall back to the raw key, then
  // to a generic label, so the caption is never empty/broken.
  const pendingPlanLabel = balance.pendingPlanName || balance.pendingPlanKey || 'your new plan';
  // formatRenewalDate tolerates null/malformed/past dates by returning null
  // (rendered as '—') — no crash on a transient stale date.
  const switchesDateLabel = formatRenewalDate(balance.pendingPlanEffectiveDate);
  // Label source for the "Keep {currentPlan}" button (reuses the planName prop).
  const currentPlanName = planName || 'your plan';

  // Statement descriptor (AC-4) — shown near whichever payment buttons are visible.
  const statementDescriptor = (
    <p className="text-xs text-gray-400 text-center">
      Payments appear as{' '}
      <span className="font-medium text-gray-600">CODELOGY* THINKOTTER</span> on your statement.
    </p>
  );

  // Inline action error (TO-LL-036) — never swallowed; visible in the active state.
  const inlineError = actionError ? (
    <p role="alert" className="text-sm text-red-600 text-center">
      {actionError}
    </p>
  ) : null;

  // Top-up packs grid (AC-5, CCM-04 config-driven). Shown ONLY to active subscribers
  // (top-up packs add coins to an existing plan — non-subscribers subscribe first).
  const topupPacksSection = (
    <div>
      <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-2">
        Top-up Packs
      </h2>

      {packsLoading && <LoadingState message="Loading top-up packs..." />}

      {!packsLoading && packsError && (
        <ErrorState
          title="Could not load top-up packs"
          message="Please try again."
          onRetry={packsRefetch}
        />
      )}

      {!packsLoading && !packsError && packs.length === 0 && (
        <EmptyState message="No top-up packs available at the moment." />
      )}

      {!packsLoading && !packsError && packs.length > 0 && (
        <div className="flex flex-wrap gap-3">
          {packs.map((pack) => (
            <TopupPackCard
              key={pack.packId}
              pack={pack}
              isPending={pendingAction === pack.packId}
              onBuy={handleBuy}
            />
          ))}
        </div>
      )}
    </div>
  );

  // Combined "Your Subscription" card header band (title + status pill). Merges the
  // former Subscription Status card and the Your Plan / Choose a Plan card into ONE card.
  const subscriptionHeader = (
    <div className="flex items-center justify-between border-b border-otter-sand bg-gradient-to-r from-otter-paper to-white px-5 pt-4 pb-3">
      <div className="flex items-center gap-2.5">
        <span
          aria-hidden="true"
          className="flex h-9 w-9 items-center justify-center rounded-xl bg-otter-cream text-lg"
        >
          🗓️
        </span>
        <div>
          <p className="font-display text-lg font-semibold leading-tight text-otter-earth-800">
            {hasActiveSubscription ? 'Your Subscription' : 'Lock your launch price'}
          </p>
          <p className="text-xs leading-tight text-gray-500">
            {hasActiveSubscription
              ? 'Manage your plan & billing'
              : 'Subscribe before usual pricing starts and keep your launch price while your plan stays active.'}
          </p>
        </div>
      </div>
      {hasActiveSubscription ? (
        <Badge variant="default" className="bg-green-600 text-white text-xs">
          Active
        </Badge>
      ) : (
        <Badge variant="outline" className="text-xs text-gray-500">
          No subscription
        </Badge>
      )}
    </div>
  );

  // Card order (top → bottom): Coins → Top-up Packs → Subscription.
  return (
    <div className="space-y-6">
      {/* 1 ▸ Coin balance (CCM-02: two distinct panels, verbatim reuse) */}
      <div>
        <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-2">
          Coin Balance
        </h2>
        <CoinBalancePanels
          balance={balance}
          isNewFamily={isNewFamily}
          onDismissWelcome={onDismissWelcome}
          welcomeCoinAmount={welcomeCoinAmount}
          onTopupDetail={() => navigate(ROUTES.PARENT_TOPUP_COINS)}
        />
      </div>

      {/* 2 ▸ Top-up packs — active subscribers only. */}
      {hasActiveSubscription && topupPacksSection}

      {/* 3 ▸ Your Subscription — single combined card (status + plan action / chooser). */}
      <div>
        <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-2">
          Your Subscription
        </h2>
        <Card className="overflow-hidden border-otter-sand">
          {subscriptionHeader}

          {hasActiveSubscription ? (
            <CardContent className="space-y-4 px-5 pt-4 pb-5">
              {/* Plan + renewal facts (values intentionally NOT bold). Plan name is
                  resolved server-side (activePlanName); falls back to a generic label. */}
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl border border-otter-sand bg-otter-paper px-3 py-2.5">
                  <p className="text-xs text-gray-500">Plan</p>
                  <p className="font-display text-xl font-medium leading-tight text-otter-earth-800">
                    {planName || 'Active plan'}
                  </p>
                </div>
                <div className="rounded-xl border border-otter-sand bg-otter-paper px-3 py-2.5">
                  {/* BUG-20260703-154837 (2026-07-03): relabel to "Cancels on <date>"
                      when a cancellation is scheduled; otherwise the existing
                      "Renews on <date>" behaviour is byte-for-byte unchanged.
                      US-002 (change-plans, T3): third ordered branch — isCancelScheduled
                      (unchanged, highest precedence) > isPendingDowngrade > default
                      'Renews on' — so cancelAt display is unaffected (AC-002.1) and the
                      default is preserved when no pending change exists (AC-002.2). */}
                  <p className="text-xs text-gray-500">
                    {isCancelScheduled ? 'Cancels on' : isPendingDowngrade ? 'Switches to ·' : 'Renews on'}
                  </p>
                  <p className="font-display text-xl font-medium leading-tight text-otter-earth-800">
                    {isCancelScheduled
                      ? (cancelLabel ?? '—')
                      : isPendingDowngrade
                        ? `${pendingPlanLabel} · on ${switchesDateLabel ?? '—'}`
                        : (renewalLabel ?? '—')}
                  </p>
                </div>
              </div>

              {/* BUG-20260703-154837 (2026-07-03): muted end-of-access caption,
                  Gate 2 approved default copy. The green "Active" badge above is
                  intentionally unchanged — access genuinely continues until the
                  effective end date. */}
              {isCancelScheduled && (
                <p className="text-xs text-gray-500">
                  Your subscription will end on this date — access continues until then.
                </p>
              )}

              {/* US-002 (change-plans, T3) / AC-002.1: reassurance caption, gated
                  INDEPENDENTLY on isPendingDowngrade (and never when a cancellation
                  is also scheduled, to avoid two competing captions) so the pending
                  and cancel signals never clobber each other. Dash-free, figure-free
                  per the product-copy ban (BR-003: informational only). */}
              {isPendingDowngrade && !isCancelScheduled && (
                <p className="text-xs text-gray-500">
                  Access continues until then. You can keep {currentPlanName} any time
                  before the switch.
                </p>
              )}

              {/* US-001 (change-plans) / T2 / AC-001.1: primary warm "Change plan" CTA
                  deep-links straight to the Stripe plan-switch screen with no interim
                  modal (AC-001.3). Replaces the former single "Manage subscription"
                  CTA as the primary action on this card (Option B). */}
              <div className="space-y-3">
                {/* US-002 (change-plans, T3) / AC-002.3: "Keep {currentPlan}" opens
                    the Stripe portal HOME (no flow, BR-004) — same door as "Manage
                    billing & cancel" — where Stripe surfaces its own cancel-this-
                    scheduled-change control. Gated independently on
                    isPendingDowngrade so it never mounts in the default/cancel
                    states. Placed above "Change plan" as the primary action while
                    a downgrade is pending. */}
                {isPendingDowngrade && (
                  <Button
                    variant="warm"
                    className="h-11 w-full"
                    onClick={handleKeepPlan}
                    disabled={pendingAction === 'keep-plan'}
                    aria-label={`Keep ${currentPlanName}`}
                  >
                    {pendingAction === 'keep-plan' ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      `Keep ${currentPlanName}`
                    )}
                  </Button>
                )}

                <Button
                  variant="warm"
                  className="h-11 w-full"
                  onClick={handleChangePlan}
                  disabled={pendingAction === 'change-plan'}
                  aria-label="Change plan"
                >
                  {pendingAction === 'change-plan' ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    'Change plan'
                  )}
                </Button>

                {/* AC-001.2: directional expectation copy only — no exact coin/price
                    figures are ever rendered here (BR-002); Stripe/webhook computes
                    proration. Uses the middle dot (·), not a hyphen. */}
                <p className="text-xs text-gray-500 text-center">
                  Upgrades apply now · downgrades at your next renewal.
                </p>

                {/* AC-001.5: light "updating…" affordance while the post-return
                    refetch is in flight (see the visibilitychange effect above). */}
                {isUpdating && (
                  <p className="text-xs text-gray-400 text-center" role="status">
                    <Loader2 className="inline h-3 w-3 animate-spin mr-1" />
                    updating…
                  </p>
                )}

                {/* AC-001.6 / BR-002 / FR-9.3: quiet payment-pending hint only — the
                    Plan/Renews-on values above are intentionally left unchanged; no
                    coin or plan-identity change is shown until invoice.paid converges
                    the state server-side. */}
                {isPastDue && (
                  <p className="text-xs text-amber-600 text-center">
                    Payment pending — we’re confirming your latest change.
                  </p>
                )}

                {/* US-001 (change-plans) / T2 / AC-001.1 + AC-001.4: the former warm
                    "Manage subscription" CTA is demoted to a quiet link. onClick is
                    UNCHANGED — it still opens CancelConfirmModal, whose confirm button
                    still calls handleManagePlan (no flow param, portal home) — the
                    existing cancel flow is preserved byte-for-byte. */}
                <Button
                  variant="link"
                  className="h-11 w-full text-gray-500"
                  onClick={() => {
                    setActionError(null);
                    setShowCancelModal(true);
                  }}
                  disabled={pendingAction === 'portal'}
                  aria-label="Manage billing & cancel"
                >
                  Manage billing & cancel
                </Button>
                {statementDescriptor}
                {inlineError}
              </div>
            </CardContent>
          ) : (
            <CardContent className="space-y-3 bg-otter-paper/40 px-5 pt-5 pb-5">
              {/* Config-driven 3-tier selector (Starter / Practice / Family). Each CTA sends
                  a planKey to createSubscriptionCheckout (PRD-Subscription-Tiers.md §7).
                  The Family planKey was renamed from the legacy 'unlimited' alias to
                  'family' on 2026-07-08. */}
              <SubscriptionPlans onChoose={handleChoosePlan} pendingPlan={pendingAction} />
              <div className="space-y-2 pt-1">
                {statementDescriptor}
                {inlineError}
              </div>
            </CardContent>
          )}
        </Card>
      </div>

      {/* ── Manage subscription confirm modal (AC-3) ────────────────────────── */}
      <CancelConfirmModal
        isOpen={showCancelModal}
        isPending={pendingAction === 'portal'}
        onConfirm={handleManagePlan}
        onCancel={() => {
          setShowCancelModal(false);
          setActionError(null);
        }}
      />
    </div>
  );
}
